/**
 * ROOM IDENTITY ON A PLAN THAT KEEPS CHANGING (spec §60B).
 *
 * A Room is a project record; a REGION is whatever the walls currently
 * enclose. The two are joined by an anchor point, and that join has to
 * survive ordinary editing: moving a wall must not silently rename the room
 * next door.
 *
 * Two rules, both deterministic and both pure:
 *
 *   1. A room's label point is always INSIDE its own region — never a
 *      centroid that falls in the hallway because the room is L-shaped.
 *   2. When the walls move, a room follows the region it was already bound
 *      to, identified by the WALLS that enclosed it. A region that still
 *      shares walls with the old one is the same room. If two regions have
 *      an equal claim (a split), or none does (a merge), that is reported
 *      honestly instead of guessed.
 *
 * Nothing here writes anything: callers decide what to do with the result.
 */
import type { PlanAnalysis, PlanRegion } from "./analysis";
import type { Point } from "./model";
import { PU_PER_INCH } from "./units";
import { closestPointOnSegment, pointInPolygon } from "./vector";

/** Candidate resolution for the interior-point search (1 ft). */
const PROBE_STEP_PU = 12 * PU_PER_INCH;
const MIN_PROBE_STEP_PU = PU_PER_INCH;

/**
 * A point guaranteed to be inside `polygon`, chosen deterministically and
 * as far from the walls as a coarse search can manage — the "pole of
 * inaccessibility", which is where a person would write the room's name.
 *
 * A centroid is used when it is genuinely inside (the common rectangular
 * case, and exactly where you would expect the label). Otherwise the
 * polygon's bounding box is probed on a fixed grid: the deepest interior
 * candidate wins, ties broken by position, so the same polygon always
 * produces the same point.
 */
export function interiorPoint(polygon: readonly Point[]): Point {
  if (polygon.length === 0) return { x: 0, y: 0 };
  const centre = polygonCentroid(polygon);
  if (pointInPolygon(centre, polygon)) return centre;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of polygon) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  // A very small room still needs a point: shrink the probe until it fits.
  let step = PROBE_STEP_PU;
  while (step > MIN_PROBE_STEP_PU && (width / step < 3 || height / step < 3)) step = Math.max(MIN_PROBE_STEP_PU, Math.floor(step / 2));

  let best: { point: Point; depth: number } | null = null;
  for (let y = minY + step / 2; y <= maxY; y += step) {
    for (let x = minX + step / 2; x <= maxX; x += step) {
      const candidate = { x: Math.round(x), y: Math.round(y) };
      if (!pointInPolygon(candidate, polygon)) continue;
      const depth = distanceToEdges(candidate, polygon);
      if (!best || depth > best.depth + 1 || (Math.abs(depth - best.depth) <= 1 && before(candidate, best.point))) {
        best = { point: candidate, depth };
      }
    }
  }
  if (best) return best.point;
  // Degenerate (a slit thinner than an inch): the first vertex is at least
  // ON the room, which beats a point in someone else's.
  return { x: polygon[0].x, y: polygon[0].y };
}

function before(a: Point, b: Point): boolean {
  return a.y < b.y || (a.y === b.y && a.x < b.x);
}

function distanceToEdges(p: Point, polygon: readonly Point[]): number {
  let min = Infinity;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    min = Math.min(min, closestPointOnSegment(p, a, b).distance);
  }
  return min;
}

function polygonCentroid(polygon: readonly Point[]): Point {
  let twiceArea = 0;
  let x = 0;
  let y = 0;
  for (let i = 0; i < polygon.length; i += 1) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const cross = a.x * b.y - b.x * a.y;
    twiceArea += cross;
    x += (a.x + b.x) * cross;
    y += (a.y + b.y) * cross;
  }
  if (twiceArea === 0) {
    const sum = polygon.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
    return { x: Math.round(sum.x / polygon.length), y: Math.round(sum.y / polygon.length) };
  }
  return { x: Math.round(x / (3 * twiceArea)), y: Math.round(y / (3 * twiceArea)) };
}

/** Where a region's name and area belong on the drawing. */
export function regionLabelPoint(region: PlanRegion): Point {
  return interiorPoint(region.interior.length >= 3 ? region.interior : region.centerline);
}

export type AnchorDecision =
  | { roomId: string; action: "keep"; regionKey: string }
  /** The anchor was outside its room; move it to a point inside. */
  | { roomId: string; action: "recentre"; regionKey: string; to: Point }
  /** The room's space was split or merged: the user must say which. */
  | { roomId: string; action: "ambiguous"; candidates: string[] }
  /** The room's space no longer exists on this plan. */
  | { roomId: string; action: "unbound" };

export interface AnchorState {
  roomId: string;
  point: Point;
  /** The region key this room was bound to when it was last reconciled. */
  regionKey: string | null;
}

/** How much of the old boundary a region still shares (0…1). */
function wallOverlap(previousWallIds: readonly string[], region: PlanRegion): number {
  if (previousWallIds.length === 0) return 0;
  const current = new Set(region.wallIds);
  let shared = 0;
  for (const id of previousWallIds) if (current.has(id)) shared += 1;
  return shared / previousWallIds.length;
}

/** The walls a region key was built from ("region:id,id,…"). */
export function wallIdsOfRegionKey(regionKey: string | null): string[] {
  if (!regionKey || !regionKey.startsWith("region:")) return [];
  const body = regionKey.slice("region:".length);
  return body === "" ? [] : body.split(",");
}

/**
 * Decide where every room's anchor belongs after a geometry change.
 *
 * The anchor is only trusted when it is still inside the region the room was
 * bound to. Otherwise the room follows its WALLS, which is what a person
 * means by "the same room": if one region still shares most of the old
 * boundary, the room moves there and its label is re-placed inside it.
 */
export function reconcileRoomAnchors(analysis: PlanAnalysis, anchors: readonly AnchorState[]): AnchorDecision[] {
  const byKey = new Map(analysis.regions.map((region) => [region.key, region]));
  const decisions: AnchorDecision[] = [];

  for (const anchor of [...anchors].sort((a, b) => a.roomId.localeCompare(b.roomId))) {
    const previous = byKey.get(anchor.regionKey ?? "");
    // Still bound where it was, and the label is still inside: nothing to do.
    if (previous && pointInPolygon(anchor.point, previous.centerline)) {
      decisions.push({ roomId: anchor.roomId, action: "keep", regionKey: previous.key });
      continue;
    }
    if (previous) {
      // The room's region survived but the label drifted out of it (a wall
      // moved past the label). Put the label back inside its own room —
      // never let it be claimed by whatever polygon it happens to sit in.
      decisions.push({
        roomId: anchor.roomId,
        action: "recentre",
        regionKey: previous.key,
        to: regionLabelPoint(previous),
      });
      continue;
    }

    const previousWalls = wallIdsOfRegionKey(anchor.regionKey);
    const scored = analysis.regions
      .map((region) => ({ region, overlap: wallOverlap(previousWalls, region) }))
      .filter((entry) => entry.overlap > 0)
      .sort((a, b) => b.overlap - a.overlap || a.region.key.localeCompare(b.region.key));

    if (scored.length === 0) {
      // No previous binding at all: fall back to wherever the anchor sits,
      // which is how a freshly placed room binds in the first place.
      const containing = analysis.regions
        .filter((region) => pointInPolygon(anchor.point, region.centerline))
        .sort((a, b) => a.floorAreaX100 - b.floorAreaX100 || a.key.localeCompare(b.key))[0];
      decisions.push(
        containing
          ? { roomId: anchor.roomId, action: "keep", regionKey: containing.key }
          : { roomId: anchor.roomId, action: "unbound" },
      );
      continue;
    }

    const best = scored[0];
    const rivals = scored.filter((entry) => Math.abs(entry.overlap - best.overlap) < 1e-9);
    if (rivals.length > 1) {
      // A split: two spaces have an equal claim on the old room. Saying
      // which one is the kitchen is the user's call, not a coin toss.
      decisions.push({
        roomId: anchor.roomId,
        action: "ambiguous",
        candidates: rivals.map((entry) => entry.region.key).sort(),
      });
      continue;
    }
    // Segment counts can favor the smaller side of a split: a kitchen
    // adjoining two bathrooms may retain more wall IDs than the living
    // room. That never authorizes moving Living's label into Kitchen.
    // Leave competing ownership for review instead of stealing a room.
    if (!pointInPolygon(anchor.point, best.region.centerline) && best.region.roomIds.some(id => id !== anchor.roomId)) {
      decisions.push({ roomId: anchor.roomId, action: "ambiguous", candidates: scored.map(entry => entry.region.key).sort() });
      continue;
    }
    // One clear successor — including the merge case, where the surviving
    // larger region still holds most of this room's walls.
    decisions.push(
      pointInPolygon(anchor.point, best.region.centerline)
        ? { roomId: anchor.roomId, action: "keep", regionKey: best.region.key }
        : { roomId: anchor.roomId, action: "recentre", regionKey: best.region.key, to: regionLabelPoint(best.region) },
    );
  }
  return decisions;
}
